import React, { useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { Option } from '~/components/Buttons/ExplicitSelectionButton/type'
import { iconSize } from '~/components/Cards/CollapsibleCard/CollapsibleCard'
import Icon from '~/components/Icon'
import { IconProps } from '~/components/Icon/type'

import { HeightClass } from '~/utils/enum/classes'

import {
  AccordionContainer,
  AccordionContent,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
  Separator
} from './styles'

export type AccordionProps = {
  items: Option[]
  defaultOpenedIndexes?: number[]
  disabled?: boolean
  hideArrowOnDisable?: boolean
  heightClass?: HeightClass
  iconColor?: IconProps['icColor']
  allowInteractionOnlyOnIconClick?: boolean
  renderAccordionTitle?: (props: AccordionItemProps) => React.ReactNode
  renderAccordionDescription?: (props: AccordionItemProps) => React.ReactNode
}

export type AccordionItemProps = {
  option: Option
  index: number
}

const Accordion = ({
  items,
  defaultOpenedIndexes = [],
  disabled = false,
  hideArrowOnDisable = false,
  heightClass = 'regular',
  iconColor = 'sanJuan',
  allowInteractionOnlyOnIconClick = false,
  renderAccordionTitle = ({ option }: { option: Option }) => option?.des,
  renderAccordionDescription = ({ option }: { option: Option }) =>
    option?.complement
}: AccordionProps) => {
  const { t } = useTranslation()

  const [openStates, setOpenStates] = useState<number[]>(defaultOpenedIndexes)

  const handleAccordionToggle = (index: number, event?: React.MouseEvent) => {
    if (allowInteractionOnlyOnIconClick && event) {
      event.preventDefault()
      return
    }

    setOpenStates((prevState) => {
      if (prevState?.includes(index)) {
        return prevState.filter((i) => i !== index)
      }
      return [...(prevState || []), index]
    })
  }

  const arrowIconSize = iconSize[heightClass] || 'regular'

  return (
    <AccordionContainer>
      {items.map((option, index) => {
        const isOpen = openStates?.includes(index)

        const isEmpty = !option?.complement

        const isDisabled = disabled || isEmpty

        const shouldRenderArrow = isDisabled ? !hideArrowOnDisable : true

        return (
          <AccordionItem
            key={index}
            disabled={isDisabled}
            $defaultOpened={isOpen}
          >
            <AccordionDetails open={isOpen}>
              <AccordionSummary
                aria-label={`${t('accordion.summary-item')} ${index + 1}`}
                tabIndex={isDisabled ? -1 : 0}
                onClick={(event) => handleAccordionToggle(index, event)}
                style={{
                  cursor:
                    isDisabled || allowInteractionOnlyOnIconClick
                      ? 'default'
                      : 'pointer'
                }}
              >
                {renderAccordionTitle({ option, index })}
                {shouldRenderArrow && (
                  <Icon
                    iconName={
                      isOpen && !isDisabled
                        ? 'DropdownArrowUp'
                        : 'DropdownArrowLow'
                    }
                    icSize={arrowIconSize}
                    icColor={iconColor}
                    nonFocusable={isDisabled}
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    aria-label={t('accordion.toggle-accordion')}
                    onClick={() => handleAccordionToggle(index)}
                  />
                )}
              </AccordionSummary>
              {!isDisabled && (
                <AccordionContent>
                  {renderAccordionDescription({ option, index })}
                </AccordionContent>
              )}
            </AccordionDetails>
            <Separator isLastOne={index === items.length - 1} />
          </AccordionItem>
        )
      })}
    </AccordionContainer>
  )
}

const AccordionTitle = ({ option }: AccordionItemProps) => <>{option?.des}</>

const AccordionDescription = ({ option }: AccordionItemProps) => (
  <>{option?.complement}</>
)

export { AccordionDescription, AccordionTitle }

export default Accordion
